import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { BRAND, WA_LINK } from "../lib/content";
import { useI18n } from "../lib/i18n";

const EXPLORE_LINKS = [
  { to: "/chambres", key: "nav.rooms", testId: "footer-nav-rooms" },
  { to: "/galerie", key: "nav.gallery", testId: "footer-nav-gallery" },
  { to: "/grand-popo", key: "nav.location", testId: "footer-nav-location" },
  { to: "/activites", key: "nav.activities", testId: "footer-nav-activities" },
  { to: "/reserver", key: "nav.book", testId: "footer-nav-book" },
];

export default function Footer() {
  return (
    <footer className="bg-[#2C241B] text-[#EFEAE1] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid md:grid-cols-12 gap-10">
        <BusinessCard />
        <ExploreLinks />
        <ContactColumn />
      </div>
      <FooterBottom />
    </footer>
  );
}

function BusinessCard() {
  return (
    <div className="md:col-span-5">
      <a
        href={BRAND.website}
        target="_blank"
        rel="noreferrer"
        data-testid="footer-business-card"
        className="block bg-[#F9F6F0] overflow-hidden max-w-md group"
        aria-label={`${BRAND.name} — owner contact card`}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_bambou-beach-booking/artifacts/sjq48iy0_BC_GHBB_HN.png"
          alt="Carte de visite Guesthouse Bambou Beach — propriétaire"
          loading="lazy"
          className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
        />
      </a>
    </div>
  );
}

function ExploreLinks() {
  const { t } = useI18n();

  return (
    <div className="md:col-span-3">
      <div className="overline text-[#EFEAE1]/80 mb-4">{t("footer.explore")}</div>
      <ul className="space-y-3 text-sm">
        {EXPLORE_LINKS.map((l) => (
          <li key={l.to}>
            <Link
              data-testid={l.testId}
              to={l.to}
              className="hover:text-white opacity-80 hover:opacity-100"
            >
              {t(l.key)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactColumn() {
  const { t } = useI18n();

  return (
    <div className="md:col-span-4">
      <div className="overline text-[#EFEAE1]/80 mb-4">{t("footer.contact")}</div>
      <ul className="space-y-3 text-sm">
        <li className="flex items-start gap-3">
          <MapPin size={16} className="mt-0.5 shrink-0 opacity-70" />
          <span>
            Quartier {BRAND.address.quartier},<br />
            {BRAND.address.commune}, {BRAND.address.country}
          </span>
        </li>
        <li className="flex items-center gap-3">
          <Phone size={16} className="opacity-70" />
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-whatsapp"
            className="hover:text-white opacity-90"
          >
            {BRAND.whatsapp}
          </a>
        </li>
        <li className="flex items-center gap-3">
          <Mail size={16} className="opacity-70" />
          <a
            href={`mailto:${BRAND.email}`}
            data-testid="footer-email"
            className="hover:text-white opacity-90"
          >
            {BRAND.email}
          </a>
        </li>
      </ul>

      <div className="mt-8">
        <div className="overline text-[#EFEAE1]/80 mb-3">{t("footer.book_on")}</div>
        <div className="flex gap-3 flex-wrap">
          <a
            href={BRAND.bookingCom}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-booking-com"
            className="text-xs tracking-[0.2em] uppercase border border-[#EFEAE1]/30 px-3 py-2 hover:bg-[#EFEAE1] hover:text-[#2C241B]"
          >
            Booking.com
          </a>
          <a
            href={BRAND.airbnb}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-airbnb"
            className="text-xs tracking-[0.2em] uppercase border border-[#EFEAE1]/30 px-3 py-2 hover:bg-[#EFEAE1] hover:text-[#2C241B]"
          >
            Airbnb
          </a>
        </div>
      </div>
    </div>
  );
}

function FooterBottom() {
  const { t } = useI18n();
  const year = new Date().getFullYear();

  return (
    <div className="border-t border-[#EFEAE1]/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs opacity-70">
        <div>
          © {year} Guesthouse Bambou Beach. {t("footer.rights")}.
        </div>
        <div>
          <a href={BRAND.website} className="hover:text-white">
            guesthousebamboubeach.com
          </a>
        </div>
      </div>
    </div>
  );
}
